import { Injectable } from '@angular/core';
import { RouteAuthorizedPoint } from '../interfaces/route-authorized-point';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Endpoints } from '../resources/endpoints';
import { RoutePointType } from '../interfaces/route-point-type';
import { Observable, Observer } from 'rxjs';
import { RouteAuthorizedPointSearchParam } from '../interfaces/route-authorized-point-search-param';
import { CargoItineraryNearnessIndex, RouteItinerary } from '../interfaces/route-itinerary';
import { RouteBasicCity } from '../interfaces/route-basic-city';
import { Cargo } from '../interfaces/cargo';
import { Utils } from '../resources/utils';
import { Origin } from '../interfaces/origin';
import { Destination } from '../interfaces/destination';
import { GoogleService } from './google.service';
import { LocationAddress } from '../interfaces/locationAddress';
import { RouteControlPoint } from '../interfaces/route-control-point';
import { Address } from '../interfaces/address';
import { AddressCargo } from '../interfaces/addressCargo';
import { AuthService } from './authentication.service';
import { DateManager } from '../managers/date.manager';
import { PlanningRoute } from '../interfaces/planning-route';
import { CriticZone } from '../interfaces/critic-zone';
import haversineDistance from 'haversine-distance';
import { Fmt } from '../messages/fmt';

export interface OptimizedRouteByAddressesResult {
  overviewPolyline: string;
  distance: number;
  duration: number;
  orderedLocations: google.maps.LatLngLiteral[];
  orderedAddressIndexes: number[];
  error?: string;
}

export interface MixedOrderedIntermediatePoint {
  source: 'cargo' | 'itinerary';
  index: number;
  location: google.maps.LatLngLiteral;
}

export interface MixedOrderedRouteResult {
  overviewPolyline: string;
  distance: number;
  duration: number;
  orderedLocations: google.maps.LatLngLiteral[];
  orderedIntermediatePoints: MixedOrderedIntermediatePoint[];
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PlanningRouteService {

  constructor(
    private endpoints: Endpoints,
    private http: HttpClient,
    private utils: Utils,
    private googleService: GoogleService,
    private authService: AuthService,
  ) { }

  /**
   * @description Calcula una ruta resumida de origen a destino, considerando las direcciones enviadas
   * @description No genera una ruta optimizada
   * @param addresses - Las direcciones a considerar
   * @returns Un objeto con el resumen de la ruta, la distancia, el tiempo de duración y el error
   */

  public async getOverviewPolylineByAddresses(addresses: Array<LocationAddress | Address | google.maps.LatLngLiteral>): Promise<{ overviewPolyline: string, distance: number, duration: number, error?: string }> {
    if (!addresses || addresses.length < 2) {
      return { overviewPolyline: null, distance: 0, duration: 0, error: 'Debe enviar al menos dos direcciones válidas' };
    }

    const locations = addresses
      .map((address) => this.getLocationFromAddress(address))
      .filter((location) => !!location);

    if (locations.length < 2)
      return { overviewPolyline: null, distance: 0, duration: 0, error: 'No se encontraron suficientes ubicaciones válidas para calcular la ruta' };

    const data = await this.googleService.getRouteData(locations, 'DRIVING', true) as {
      cargoDistancy: number,
      cargoEstimatedTime: number,
      cargoRoute: google.maps.DirectionsResult
    };

    const route = data && data.cargoRoute && data.cargoRoute.routes && data.cargoRoute.routes.length
      ? data.cargoRoute.routes[0]
      : null;
    const overviewPolyline: any = route && route.overview_polyline ? route.overview_polyline : null;

    if (!overviewPolyline)
      return { overviewPolyline: null, distance: 0, duration: 0, error: 'No fue posible obtener el resumen de la ruta' };

    if (typeof overviewPolyline === 'string')
      return { overviewPolyline: overviewPolyline, distance: data.cargoDistancy, duration: data.cargoEstimatedTime, error: null };

    if (typeof overviewPolyline.points === 'string')
      return { overviewPolyline: overviewPolyline.points, distance: data.cargoDistancy, duration: data.cargoEstimatedTime, error: null };

    return { overviewPolyline: null, distance: 0, duration: 0, error: 'Formato de ruta inválido' };
  }

  private getLocationFromAddress(address: LocationAddress | Address | google.maps.LatLngLiteral): google.maps.LatLngLiteral | null {
    if (!address || address.lat === undefined || address.lng === undefined || address.lat === null || address.lng === null)
      return null;
    return { lat: Number(address.lat), lng: Number(address.lng) };
  }

  public getPointTypes(): Observable<RoutePointType[]> {
    return new Observable<RoutePointType[]>((observer: Observer<RoutePointType[]>) => {
      this.http.get(`${environment.urlServerTeclogi}${this.endpoints.urlCatalog}/${this.endpoints.AuthorizedStopPointsTypes}`)
        .subscribe((result: { catalog: RoutePointType[] }) => {
          observer.next(result.catalog);
          observer.complete();
        });
    });
  }

  public createAuthorizedPoint(point: RouteAuthorizedPoint) {
    return this.http.post(`${environment.urlServerTeclogi}${this.endpoints.createAuthorizedPoint}`, point);
  }

  public createAuthorizedPointBatch(points: RouteAuthorizedPoint[]) {
    return this.http.post(`${environment.urlServerTeclogi}${this.endpoints.createAuthorizedPointBatch}`, points);
  }

  public createRouteItinerary(itinerary: RouteItinerary): Observable<RouteItinerary> {
    return this.http.post<RouteItinerary>(`${environment.urlServerTeclogi}${this.endpoints.createRouteItinerary}`, itinerary);
  }

  public updateAuthorizedPoint(point: RouteAuthorizedPoint) {
    return this.http.put(`${environment.urlServerTeclogi}${this.endpoints.updateAuthorizedPoint}`, point);
  }

  public updateCity(city: RouteBasicCity): Observable<RouteBasicCity> {
    return this.http.put(`${environment.urlServerTeclogi}${this.endpoints.updateCity}`, city) as unknown as Observable<RouteBasicCity>;
  }

  public deactivateAuthorizedPoint(pointId: string) {
    return this.http.delete(`${environment.urlServerTeclogi}${this.endpoints.deactiveAuthorizedPoint}?id=${pointId}`);
  }

  public getAuthorizedPoints(params?: RouteAuthorizedPointSearchParam) {
    return this.http.get(`${environment.urlServerTeclogi}${this.endpoints.getAuthorizedPoints}`, { params: params as any });
  }

  public createCriticZone(zone: CriticZone) {
    return this.http.post(`${environment.urlServerTeclogi}${this.endpoints.createCriticZone}`, zone);
  }

  public updateCriticZone(zone: CriticZone) {
    return this.http.put(`${environment.urlServerTeclogi}${this.endpoints.updateCriticZone}`, zone);
  }

  public getCriticZones(pageKey?: number, pageSize?: number) {
    if (this.utils.isDefined(pageKey) && this.utils.isDefined(pageSize)) {
      const params = new HttpParams().set('pageKey', pageKey.toString()).set('pageSize', pageSize.toString());
      return this.http.get(`${environment.urlServerTeclogi}${this.endpoints.getCriticZones}`, { params });
    }
    return this.http.get(`${environment.urlServerTeclogi}${this.endpoints.getCriticZones}`);
  }

  public addItineraryCriticZone(itineraryId: string, criticZoneIds: string[]) {
    return this.http.post(`${environment.urlServerTeclogi}${Fmt.string(this.endpoints.addItineraryCriticZone, itineraryId)}`, criticZoneIds);
  }

  public removeItineraryCriticZone(itineraryId: string, criticZoneIds: string[]) {
    return this.http.request('DELETE', `${environment.urlServerTeclogi}${Fmt.string(this.endpoints.removeItineraryCriticZone, itineraryId)}`,
      { body: criticZoneIds, headers: new HttpHeaders({ 'Content-Type': 'application/json' }) });
  }

  public getEncodedPolylineFromRoute(route: google.maps.DirectionsRoute): string {
    if (!route || !route.overview_polyline) {
      return null;
    }
    const overviewPolyline: any = route.overview_polyline;
    if (typeof overviewPolyline === 'string') {
      return overviewPolyline;
    }
    if (overviewPolyline.points && typeof overviewPolyline.points === 'string') {
      return overviewPolyline.points;
    }
    return null;
  }

  public getCriticZonePath(encodedPolyline: string): google.maps.LatLngLiteral[] {
    if (!encodedPolyline) {
      return [];
    }
    try {
      return this.utils.decodePolyline(encodedPolyline);
    } catch (error) {
      return [];
    }
  }

  public getCriticZoneCorridorStyles(): Array<{
    strokeColor: string;
    strokeOpacity: number;
    strokeWeight: number;
    zIndex: number;
  }> {
    return [
      { strokeColor: '#EE3344', strokeOpacity: 0.18, strokeWeight: 28, zIndex: 40 },
      { strokeColor: '#EE3344', strokeOpacity: 0.35, strokeWeight: 16, zIndex: 41 },
      { strokeColor: '#DC1428', strokeOpacity: 0.85, strokeWeight: 6, zIndex: 42 }
    ];
  }

  public createCriticZoneCorridor(map: google.maps.Map, path: google.maps.LatLngLiteral[]): google.maps.Polyline[] {
    if (!map || !path || path.length < 2) {
      return [];
    }
    return this.getCriticZoneCorridorStyles().map((style) => {
      return new google.maps.Polyline({
        path,
        strokeColor: style.strokeColor,
        strokeOpacity: style.strokeOpacity,
        strokeWeight: style.strokeWeight,
        geodesic: true,
        clickable: false,
        zIndex: style.zIndex,
        map
      });
    });
  }

  public getRoutes(origin?: string, destination?: string, includeItineraries: boolean = true): Observable<PlanningRoute[]> {
    const params = {};
    params['active'] = 'true';
    !!origin ? params['origin'] = origin : 0;
    !!destination ? params['destination'] = destination : 0;
    !includeItineraries && (params['includeItineraries'] = 'false');
    return this.http.get<PlanningRoute[]>(`${environment.urlServerTeclogi}${this.endpoints.listRoutes}`, { params });
  }

  public getRoute(id: string) {
    return this.http.get(`${environment.urlServerTeclogi}${this.endpoints.listRoutes}/${id}`);
  }

  public getItinerary(id: string): Observable<RouteItinerary> {
    return this.http.get<RouteItinerary>(`${environment.urlServerTeclogi}${this.endpoints.routeItinerary}/${id}`);
  }

  public selectItinerary(id: string) {
    return this.http.put(`${environment.urlServerTeclogi}${this.endpoints.routeItinerarySelect}/?id=${id}`, null);
  }

  public saveItineraryControlPoints(controlPoints: RouteControlPoint[], itineraryId: string) {
    const url = `routeplan/itinerary/${itineraryId}/control-points`;
    return this.http.put(`${environment.urlServerTeclogi}${url}`, controlPoints);
  }

  public addItineraryAuthorizedPoint(itineraryId: string, authorizedPointIds: string[]) {
    const url = `routeplan/itinerary/${itineraryId}/authorized-stops`;
    return this.http.post(`${environment.urlServerTeclogi}${url}`, authorizedPointIds);
  }

  public removeItineraryAuthorizedPoint(itineraryId: string, authorizedPointIds: string[]) {
    const url = `routeplan/itinerary/${itineraryId}/authorized-stops`;
    const requestBody = authorizedPointIds;

    return this.http.request('DELETE', `${environment.urlServerTeclogi}${url}`, {
      body: requestBody,
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    });
  }

  public removeUnusedControlPoint(itinerary: RouteItinerary, controlPointIds: string[]) {
    const url = `routeplan/itinerary/${itinerary.id}/control-points`;
    const requestBody = controlPointIds;

    return this.http.request('DELETE', `${environment.urlServerTeclogi}${url}`, {
      body: requestBody,
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    });
  }

  public saveItineraryChanges(itinerary: RouteItinerary) {
    const itineraryWithoutControlPoints = { ...itinerary };
    itineraryWithoutControlPoints.controlPoints = [];
    const url = `routeplan/itinerary`;
    return this.http.put(`${environment.urlServerTeclogi}${url}`, itineraryWithoutControlPoints);
  }

  public createRouteItineraryFromCargo(cargo: Cargo): Promise<RouteItinerary | string> {
    return new Promise(async (resolve, reject) => {
      try {
        const originCity: Origin = this.utils.getNestedValue(cargo, "cargoFeature.uploadDownload.origin");
        if (!originCity || !originCity.name || !originCity.municipalityCode || !originCity.addresses)
          reject("Ocurrió un error al acceder a la ciudad de origen");
        const destinations: Destination[] = this.utils.getNestedValue(cargo, "cargoFeature.uploadDownload.destination");
        if (!destinations || !destinations.length)
          reject("Ocurrió un error al acceder a la ciudad de destino");
        const destinationCity: Destination = destinations[destinations.length - 1];

        const locations: AddressCargo[] = [];
        if (!originCity.addresses[0] || !originCity.addresses[0].location)
          reject("Ocurrió un error al acceder a la posición geográfica de la ciudad de origen");
        locations[0] = originCity.addresses[0];
        destinations.forEach(destination => {
          if (destination && destination.addresses && destination.addresses[0] && destination.addresses[0].location)
            locations.push(destination.addresses[0]);
        });

        const controlPoints: RouteControlPoint[] = [];
        locations.filter((loc, i) => i !== 0 && i !== locations.length - 1).forEach((location, i) => {
          const controlPoint: RouteControlPoint = {
            location: location.location,
            name: location.address,
            address: location.address,
            order: i + 1,
            fingerprint: {
              userId: this.authService
                .getUserSession()
                .information.document,
              userName: this.authService.getUserSession().information.name,
              date: DateManager.dateToString(new Date()),
            }
          };
          controlPoints.push(controlPoint);
        });

        const data = await this.googleService.getRouteData(locations.map(loc => loc.location), 'DRIVING', true) as { cargoDistancy: number, cargoEstimatedTime: number, cargoRoute: google.maps.DirectionsResult };
        if (!data || !data.cargoDistancy || !data.cargoEstimatedTime || !data.cargoRoute)
          reject("No fue posible crear el plan de ruta de este servicio");

        const render = new google.maps.DirectionsRenderer();
        render.setDirections(data.cargoRoute);

        const itinerary: RouteItinerary = {
          name: `Ruta autogenerada ${originCity.name} - ${destinationCity.name}`,
          overviewPolylines: render.getDirections().routes[0].overview_polyline,
          estimatedTime: data.cargoEstimatedTime,
          estimatedDistance: data.cargoDistancy,
          mustSleep: data.cargoEstimatedTime > (14 * 60 * 60),
          originPoint: {
            id: originCity.municipalityCode,
            name: originCity.name,
            location: locations[0].location
          },
          destinationPoint: {
            id: destinationCity.municipalityCode,
            name: destinationCity.name,
            location: locations[locations.length - 1].location
          },
          authorizedStops: [],
          criticZones: [],
          controlPoints
        };

        resolve(itinerary);
      } catch (e) {
        console.error(e)
        reject("No fue posible crear el plan de ruta de este servicio");
      }


    });
  }

  public async checkPlanningRoute(cargo: Cargo, hasPermission: boolean): Promise<{ success: boolean, message?: string }> {
    let route: PlanningRoute;
    try { route = await this.findRouteByCargo(cargo) } catch (e) { }
    const noRouteOrItineraries: boolean = !route || !route.id || !route.itineraries || !route.itineraries.length;
    const itinerariesIndexes: { id: string, indexes?: CargoItineraryNearnessIndex[] }[] = route && route.itineraries && route.itineraries.length
      ? route.itineraries.map(itinerary => { if (itinerary.active) return { id: itinerary.id, indexes: this.getCargoNearnessIndexes(cargo, itinerary) } })
      : [];
    let bestItinerary: { id: string, indexes?: CargoItineraryNearnessIndex[] } | null = itinerariesIndexes.length
      ? itinerariesIndexes.find(itinerary => itinerary && itinerary.indexes && itinerary.indexes.length && itinerary.indexes.every(index => (index && index.index >= 0.7) || hasPermission))
      : null;
    if (noRouteOrItineraries || !bestItinerary) {
      try {
        const body = await this.createRouteItineraryFromCargo(cargo);
        if (typeof body === 'string') {
          if (body.includes('No se encontraron rutas entre las ubicaciones especificadas')) {
            return { success: false, message: "No fue posible generar el plan de ruta. Por favor, utilice el módulo de rutas para crear un plan manual y vuelva a intentarlo." };
          }
          return { success: false, message: body };
        }
        let newItinerary = await this.createRouteItinerary(body).toPromise();
        bestItinerary = { id: newItinerary.id };
        route = await this.findRouteByCargo(cargo);
      } catch (e) {
        return { success: false, message: "No fue posible generar el plan de ruta. Por favor, utilice el módulo de rutas para crear un plan manual y vuelva a intentarlo." };
      }
    }
    if (route && route.id) {
      let completeBestItinerary: RouteItinerary;
      try { completeBestItinerary = await this.getItinerary(bestItinerary.id).toPromise(); } catch (e) { };
      let cargoOverviewPolyline: { overviewPolyline: string, distance: number, duration: number, error?: string };
      try { cargoOverviewPolyline = await this.getCargoOverviewPolyline(cargo, completeBestItinerary); } catch (e) { };
      if (cargoOverviewPolyline && cargoOverviewPolyline.error)
        return { success: false, message: cargoOverviewPolyline.error };
      let response: Cargo[];
      try {
        response = await this.setCargoRouteAndItinerary([{
          cargoId: cargo.id,
          routePlanId: route.id,
          itineraryId: bestItinerary.id,
          itineraryOverviewPolylines: cargoOverviewPolyline ? cargoOverviewPolyline.overviewPolyline : completeBestItinerary.overviewPolylines
        }]).toPromise()
      } catch (e) { };
      if (response && response.length && response[0].routePlanId && response[0].itineraryId) {
        return { success: true, message: "Se ha asignado un plan de ruta al servicio" };
      } else return { success: false, message: "Ocurrió un error al asignar el plan de ruta" };
    }
    return { success: false, message: "Ocurrió un error al asignar el plan de ruta" };
  }


  public async findRouteByCargo(cargo: Cargo): Promise<PlanningRoute> {
    const originCity: Origin = this.utils.getNestedValue(cargo, "cargoFeature.uploadDownload.origin");
    if (!originCity || !originCity.name || !originCity.municipalityCode || !originCity.addresses)
      return null;
    const destinations: Destination[] = this.utils.getNestedValue(cargo, "cargoFeature.uploadDownload.destination");
    if (!destinations || !destinations.length)
      return null;
    const destinationCity: Destination = destinations[destinations.length - 1];
    try {
      const routes = await this.getRoutes(originCity.name, destinationCity.name).toPromise();
      return routes && routes.length ? routes[0] : null;
    } catch (e) {
      return null;
    }
  }

  public getCargoNearnessIndexes(cargo: Cargo, itinerary: RouteItinerary): CargoItineraryNearnessIndex[] {
    try {
      const distances = [];
      const cargoLocations: { address: string, lat: number, lng: number }[] = [];
      const itineraryLocations: { lat: number, lng: number }[] = [];
      const origin = this.utils.getNestedValue(cargo, "cargoFeature.uploadDownload.origin");
      const destinations = this.utils.getNestedValue(cargo, "cargoFeature.uploadDownload.destination");
      origin.addresses.forEach(address => cargoLocations.push({ address: address.address, lat: address.location.lat, lng: address.location.lng }));
      destinations.forEach(destination => destination.addresses.forEach(address => cargoLocations.push({ address: address.address, lat: address.location.lat, lng: address.location.lng })));
      itinerary.originPoint.location && itineraryLocations.push(itinerary.originPoint.location);
      if (itinerary.overviewPolylines)
        itineraryLocations.push(...this.utils.decodePolyline(itinerary.overviewPolylines));
      else if (itinerary.controlPoints && itinerary.controlPoints.length)
        itinerary.controlPoints.forEach(controlPoint => itineraryLocations.push(controlPoint.location));
      itinerary.destinationPoint.location && itineraryLocations.push(itinerary.destinationPoint.location);
      cargoLocations.forEach(cargoLocation => {
        const locationDistances = itineraryLocations.map(itineraryLocation => {
          const distance = haversineDistance({ lat: cargoLocation.lat, lng: cargoLocation.lng }, itineraryLocation);
          return distance / 1000;
        });
        distances.push(Math.min(...locationDistances));
      });
      const indexes = distances.map((distance, index) => {
        const threshold = index === 0 || index === distances.length - 1 ? 15 : 7;
        const maxDistanceAcceptable = index === 0 || index === distances.length - 1 ? 50 : 30;
        return Math.floor(this.getNearnessIndex(distance, threshold, maxDistanceAcceptable) * 100) / 100
      });
      return cargoLocations.map((cargoLocation, index) => ({ address: cargoLocation.address, index: indexes[index] }));
    } catch (e) {
      return [];
    }
  }

  public getNearnessIndex(distance: number, threshold = 7, maxDistanceAcceptable = 30): number {
    if (distance < 0 || threshold <= 0)
      return 0;
    const index = Math.exp(-distance / maxDistanceAcceptable);
    if (distance < threshold)
      return Math.max(0.9, index);
    return index;
  }

  private orderItineraryPointsByCargoDistance(
    cargoLocations: { address: string, lat: number, lng: number }[],
    itineraryPoints: { address: string, lat: number, lng: number }[]
  ): Array<{ address: string, lat: number, lng: number }> {
    if (!itineraryPoints || !itineraryPoints.length)
      return [...cargoLocations];
    const itineraryWithPosition = itineraryPoints.map((itineraryPoint) => {
      const distances = cargoLocations.map((cargoLocation) => {
        const distance = haversineDistance(
          { lat: itineraryPoint.lat, lng: itineraryPoint.lng },
          { lat: cargoLocation.lat, lng: cargoLocation.lng }
        );
        return distance / 1000;
      });
      const bestSegmentIndex = distances.indexOf(Math.min(...distances));
      if (bestSegmentIndex === 0) return { point: itineraryPoint, position: 0 };
      if (bestSegmentIndex === cargoLocations.length - 1)
        return { point: itineraryPoint, position: cargoLocations.length - 2 };
      const leftSegmentDistance = distances[bestSegmentIndex - 1];
      const rightSegmentDistance = distances[bestSegmentIndex + 1];
      if (leftSegmentDistance < rightSegmentDistance) return { point: itineraryPoint, position: bestSegmentIndex - 1 };
      return { point: itineraryPoint, position: bestSegmentIndex + 1 };
    });

    let orderedLocations: { address: string, lat: number, lng: number }[] = [];
    for (let i = 0; i < cargoLocations.length; i++) {
      orderedLocations.push(cargoLocations[i]);
      itineraryWithPosition.forEach(point => {
        if (point.position === i) orderedLocations.push(point.point);
      });
    }

    return orderedLocations;
  }

  public async getCargoOverviewPolyline(cargo: Cargo, itinerary: RouteItinerary): Promise<{ overviewPolyline: string, distance: number, duration: number, error?: string }> {
    try {
      const cargoLocations: { address: string, lat: number, lng: number }[] = [];
      const itineraryControlPoints: { address: string, lat: number, lng: number }[] = [];
      const origin = this.utils.getNestedValue(cargo, "cargoFeature.uploadDownload.origin");
      const destinations = this.utils.getNestedValue(cargo, "cargoFeature.uploadDownload.destination");
      origin.addresses.forEach(address => cargoLocations.push({ address: address.address, lat: address.location.lat, lng: address.location.lng }));
      destinations.forEach(destination => destination.addresses.forEach(address => cargoLocations.push({ address: address.address, lat: address.location.lat, lng: address.location.lng })));
      if (itinerary && itinerary.controlPoints && itinerary.controlPoints.length)
        itinerary.controlPoints.forEach(controlPoint => itineraryControlPoints.push({ address: controlPoint.address, lat: controlPoint.location.lat, lng: controlPoint.location.lng }));
      if (!cargoLocations.length) return { overviewPolyline: null, distance: 0, duration: 0, error: 'No se encontraron ubicaciones para el servicio' };
      if (cargoLocations.length + itineraryControlPoints.length >= 25) return { overviewPolyline: null, distance: 0, duration: 0, error: 'No se puede generar una ruta con más de 25 puntos' };
      if (cargoLocations.length >= 3 && itineraryControlPoints.length) {
        const orderedLocations = this.orderItineraryPointsByCargoDistance(cargoLocations, itineraryControlPoints);
        const data = await this.getOverviewPolylineByAddresses(orderedLocations);
        if (data.error) return { overviewPolyline: null, distance: 0, duration: 0, error: data.error };
        return { overviewPolyline: data.overviewPolyline, distance: data.distance, duration: data.duration };
      } else {
        const locations = itineraryControlPoints.length
          ? [cargoLocations[0], ...itineraryControlPoints, cargoLocations[cargoLocations.length - 1]]
          : cargoLocations;
        const data = await this.getOverviewPolylineByAddresses(locations);
        if (data.error) return { overviewPolyline: null, distance: 0, duration: 0, error: data.error };
        return { overviewPolyline: data.overviewPolyline, distance: data.distance, duration: data.duration };
      }
    } catch (e) {
      console.error(e);
      return { overviewPolyline: null, distance: 0, duration: 0, error: 'Ocurrió un error al generar la ruta' };
    }
  }

  public setCargoRouteAndItinerary(body: { cargoId: string, itineraryId: string, itineraryOverviewPolylines?: string, routePlanId: string }[]): Observable<Cargo[]> {
    return this.http.put<Cargo[]>(`${environment.urlServerTeclogi}${this.endpoints.setCargoPlanItinerary}`, body);
  }

}
